/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import { useDispatch, useSelector } from 'react-redux';
import { getAnalyticCurve } from 'actions';
import moment from 'moment';
import PlaceholderSpinner from 'components/kea/placeholders/PlaceholderSpinner';
import CardContainer from 'components/kea/CardContainer';
import DashboardStub from 'components/kea/DashboardStub';
import { groupCurveByDays, theCurveIsMostlyFull } from 'util/appUtils';
import { MenuItem, Select } from '@material-ui/core';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { analyticsChartTheme, formatCompactMetric } from 'util/chartUtils';
import styles from './QueryTimeline.module.scss';

const SelectGrouping = ({ onChange, value, localizeMessage }) => (
  <Select
    onChange={onChange}
    style={{ minWidth: 176 }}
    value={value}
    inputProps={{ 'aria-label': localizeMessage('analytics.timeline.grouping.label') }}
  >
    <MenuItem value={false}>{localizeMessage('analytics.timeline.grouping.auto')}</MenuItem>
    <MenuItem value={1}>{localizeMessage('analytics.timeline.grouping.day')}</MenuItem>
    <MenuItem value={7}>{localizeMessage('analytics.timeline.grouping.week')}</MenuItem>
    <MenuItem value={30}>{localizeMessage('analytics.timeline.grouping.month')}</MenuItem>
  </Select>
);

const QueryTimeline = ({ match }) => {
  const localizeMessage = useLocalizedMessage();
  const { params: { query } } = match;
  const dispatch = useDispatch();
  const [groupBy, setGroupBy] = useState(false);
  const selectHandler = (e) => setGroupBy(e.target.value);

  const {
    analytics: { search, timeframe },
    app: { currentShop },
  } = useSelector((store) => store);
  const { curve: { isFetched, isFetching } } = search;

  useEffect(() => {
    dispatch(getAnalyticCurve(currentShop, timeframe, 'all', query));
  }, [timeframe, query]);

  const dateAlias = '_id';
  const wantedFields = ['click', 'noaction', 'total', 'refine'];
  function autoGroupCurve(arr) {
    let result = arr;
    if (arr.length > 27) {
      const granularity = arr.length > 175 ? 30 : 7;
      const ignoreGrouping = (arr.length < 45) && theCurveIsMostlyFull(arr, 'total', 0.5);
      if (!ignoreGrouping) {
        result = groupCurveByDays(arr, granularity, dateAlias, wantedFields);
      }
    }
    return result;
  }

  const prepareData = (curve, groupByDays) => {
    if (!curve.data) return [];
    let curveData;

    if (groupByDays) {
      curveData = groupCurveByDays(curve.data, groupByDays, dateAlias, wantedFields);
    } else {
      curveData = autoGroupCurve(curve.data, groupByDays);
    }

    return curveData.map(row => ({
      label: moment([
        row[dateAlias].year,
        row[dateAlias].month - 1,
        row[dateAlias].day,
      ]).format('YYYY-MM-DD'),
      noaction: Number(row.noaction) || 0,
      refine: Number(row.refine) || 0,
      click: Number(row.click) || 0,
    }));
  };

  const chartData = isFetched ? prepareData(search.curve, groupBy) : [];
  const hasData = chartData.some(row => row.noaction + row.refine + row.click > 0);

  return (
    <CardContainer
      headerId="search.analytics.queryDetail.timeline.header"
      headerPostfix={query}
      actionComponent={(
        <SelectGrouping
          onChange={selectHandler}
          value={groupBy}
          localizeMessage={localizeMessage}
        />
      )}
    >
      {isFetching || !isFetched ? <PlaceholderSpinner height={340} /> : null}
      {isFetched && !isFetching && !hasData ? (
        <DashboardStub variant="bars" messageId="analytics.query.timeline.empty" />
      ) : null}
      {isFetched && !isFetching && hasData ? (
        <div className={styles.chartFrame}>
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={chartData} margin={{ top: 12, right: 18, left: 0, bottom: 8 }}>
              <CartesianGrid stroke={analyticsChartTheme.grid} vertical={false} strokeDasharray="2 7" />
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                minTickGap={24}
                tick={{ fill: analyticsChartTheme.muted, fontSize: 10 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                width={44}
                allowDecimals={false}
                tickFormatter={formatCompactMetric}
                tick={{ fill: analyticsChartTheme.muted, fontSize: 10 }}
              />
              <Tooltip />
              <Legend iconType="circle" />
              <Bar
                dataKey="noaction"
                name={localizeMessage('analytics.timeline.chart.noActions')}
                stackId="actions"
                fill={analyticsChartTheme.zeroResults}
                isAnimationActive={false}
              />
              <Bar
                dataKey="refine"
                name={localizeMessage('analytics.timeline.chart.otherActions')}
                stackId="actions"
                fill={analyticsChartTheme.clickRate}
                isAnimationActive={false}
              />
              <Bar
                dataKey="click"
                name={localizeMessage('analytics.timeline.chart.productClicks')}
                stackId="actions"
                fill={analyticsChartTheme.searches}
                radius={[3, 3, 0, 0]}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : null}
    </CardContainer>
  );
};

export default QueryTimeline;
